import { useState, useMemo, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Plus, Search, LayoutList, Kanban } from "lucide-react"
import { ExportButton } from "@/components/export-button"
import { LoadMoreButton } from "@/components/load-more-button"
import { useServiceTickets } from "@/hooks/use-service-tickets"
import { useColumnPreferences } from "@/hooks/use-column-preferences"
import { useFilterParams } from "@/hooks/use-filter-params"
import { useBulkDelete, useBulkUpdate, useBulkTag } from "@/hooks/use-bulk-actions"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { ColumnSelector, type ColumnOption } from "@/components/column-selector"
import { SortableHeader } from "@/components/sortable-header"
import { BulkActionBar, buildDeleteAction, buildTagAction, buildAssignAction, buildStatusAction, buildPriorityAction } from "@/components/bulk-action-bar"
import { BulkDeleteDialog } from "@/components/bulk-delete-dialog"
import { BulkAssignDialog } from "@/components/bulk-assign-dialog"
import { BulkStatusDialog } from "@/components/bulk-status-dialog"
import { BulkTagDialog } from "@/components/bulk-tag-dialog"
import { FilterPanel, SelectFilter, DateRangeFilter } from "@/components/filters"
import { createSelectColumn } from "@/lib/bulk-selection"
import { formatDate, sortingStateToParam, buildDetailUrl } from "@/lib/utils"
import { toast } from "sonner"
import {
  type SortingState,
  type RowSelectionState,
  type ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import type { ServiceTicket } from "@/lib/types"

const TICKET_FILTER_KEYS = [
  "status", "priority", "assigned_to_id", "created_at_from", "created_at_to",
] as const

const columnHelper = createColumnHelper<ServiceTicket>()

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  low: { label: "Niski", color: "#6B7280" },
  medium: { label: "Sredni", color: "#3B82F6" },
  high: { label: "Wysoki", color: "#F59E0B" },
  urgent: { label: "Pilny", color: "#EF4444" },
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  open: { label: "Otwarty", color: "var(--primary)" },
  resolved: { label: "Rozwiazany", color: "#22C55E" },
  closed: { label: "Zamkniety", color: "#6B7280" },
}

const TICKET_COLUMN_IDS = ["title", "phase", "priority", "status", "assigned_to", "created_at"]
const DEFAULT_VISIBLE = ["title", "phase", "priority", "status", "assigned_to", "created_at"]

const COLUMN_LABELS: Record<string, string> = {
  title: "Zgloszenie",
  phase: "Etap",
  priority: "Priorytet",
  status: "Status",
  assigned_to: "Przypisany",
  created_at: "Utworzono",
}

const columnOptions: ColumnOption[] = TICKET_COLUMN_IDS.map((id) => ({
  id,
  label: COLUMN_LABELS[id],
}))

export function ServiceTicketsListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showAssignDialog, setShowAssignDialog] = useState(false)
  const [showStatusDialog, setShowStatusDialog] = useState(false)
  const [showPriorityDialog, setShowPriorityDialog] = useState(false)
  const [showTagDialog, setShowTagDialog] = useState(false)

  const [cursor, setCursor] = useState<string | undefined>()
  const [allTickets, setAllTickets] = useState<ServiceTicket[]>([])
  const prevKeyRef = useRef("")

  const { filters, setFilter, clearFilters, activeFilterCount } = useFilterParams(TICKET_FILTER_KEYS)

  const bulkDeleteMutation = useBulkDelete("service_tickets")
  const bulkUpdateMutation = useBulkUpdate("service_tickets")
  const bulkTagMutation = useBulkTag("service_tickets")

  const sortParam = sortingStateToParam(sorting)
  const { data, isLoading, isFetching } = useServiceTickets({
    search: search || undefined,
    sort: sortParam,
    cursor,
    ...filters,
  })

  const filterKey = JSON.stringify({ search, sort: sortParam, ...filters })
  useEffect(() => {
    if (filterKey !== prevKeyRef.current) {
      prevKeyRef.current = filterKey
      setCursor(undefined)
      setAllTickets([])
    }
  }, [filterKey])

  useEffect(() => {
    if (data?.data) {
      if (cursor) {
        setAllTickets((prev) => [...prev, ...data.data])
      } else {
        setAllTickets(data.data)
      }
    }
  }, [data])

  const tickets = allTickets
  const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k])
  const selectedCount = selectedIds.length

  const columns = useMemo(
    () => [
      createSelectColumn<ServiceTicket>(),
      columnHelper.accessor("title", {
        header: ({ column }) => <SortableHeader column={column}>Zgloszenie</SortableHeader>,
        cell: (info) => (
          <div>
            <span className="font-medium text-foreground">
              {info.getValue()}
            </span>
            {info.row.original.company && (
              <p className="text-xs text-muted-foreground">
                {info.row.original.company.name}
              </p>
            )}
          </div>
        ),
      }),
      columnHelper.accessor("phase", {
        header: () => "Etap",
        enableSorting: false,
        cell: (info) => {
          const phase = info.getValue()
          return (
            <div className="flex items-center gap-2">
              <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: phase.color }} />
              <span className="text-sm text-foreground">{phase.name}</span>
            </div>
          )
        },
      }),
      columnHelper.accessor("priority", {
        header: ({ column }) => <SortableHeader column={column}>Priorytet</SortableHeader>,
        cell: (info) => {
          const priority = info.getValue()
          const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium
          return (
            <Badge variant="outline" className="text-[10px]" style={{ borderColor: config.color, color: config.color }}>
              {config.label}
            </Badge>
          )
        },
      }),
      columnHelper.accessor("status", {
        header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
        cell: (info) => {
          const status = info.getValue()
          const config = STATUS_CONFIG[status] || STATUS_CONFIG.open
          return (
            <Badge variant="outline" className="text-xs" style={{ borderColor: config.color, color: config.color }}>
              {config.label}
            </Badge>
          )
        },
      }),
      columnHelper.accessor("assigned_to", {
        header: () => "Przypisany",
        enableSorting: false,
        cell: (info) => {
          const user = info.getValue()
          return (
            <span className="text-sm text-muted-foreground">
              {user ? `${user.first_name} ${user.last_name}` : "\u2014"}
            </span>
          )
        },
      }),
      columnHelper.accessor("created_at", {
        header: ({ column }) => <SortableHeader column={column}>Utworzono</SortableHeader>,
        cell: (info) => (
          <span className="text-sm text-muted-foreground">
            {formatDate(info.getValue())}
          </span>
        ),
      }),
    ],
    []
  )

  const {
    columnVisibility,
    setColumnVisibility,
    visibleCount,
    totalCount,
    toggleColumn,
    showAll,
    hideAll,
    isColumnVisible,
  } = useColumnPreferences("service_tickets", TICKET_COLUMN_IDS, DEFAULT_VISIBLE)

  const handleBulkDelete = async () => {
    const result = await bulkDeleteMutation.mutateAsync({ ids: selectedIds })
    toast.success(`Usunieto ${result.deleted} zgloszen`)
    setRowSelection({})
  }

  const handleBulkAssign = async (userId: string) => {
    const result = await bulkUpdateMutation.mutateAsync({
      ids: selectedIds,
      changes: { assigned_to_id: userId },
    })
    toast.success(`Zaktualizowano ${result.updated} zgloszen`)
    setRowSelection({})
  }

  const handleBulkStatus = async (status: string) => {
    const result = await bulkUpdateMutation.mutateAsync({
      ids: selectedIds,
      changes: { status },
    })
    toast.success(`Zaktualizowano ${result.updated} zgloszen`)
    setRowSelection({})
  }

  const handleBulkPriority = async (priority: string) => {
    const result = await bulkUpdateMutation.mutateAsync({
      ids: selectedIds,
      changes: { priority },
    })
    toast.success(`Zaktualizowano ${result.updated} zgloszen`)
    setRowSelection({})
  }

  const handleBulkTag = async (tagNames: string[]) => {
    const result = await bulkTagMutation.mutateAsync({
      ids: selectedIds,
      tag_names: tagNames,
    })
    toast.success(`Otagowano ${result.tagged} zgloszen`)
    setRowSelection({})
  }

  const bulkActions = [
    buildAssignAction(() => setShowAssignDialog(true), "Przypisz"),
    buildStatusAction(() => setShowStatusDialog(true)),
    buildPriorityAction(() => setShowPriorityDialog(true)),
    buildTagAction(() => setShowTagDialog(true)),
    buildDeleteAction(() => setShowDeleteDialog(true)),
  ]

  const table = useReactTable({
    data: tickets,
    columns: columns as ColumnDef<ServiceTicket, unknown>[],
    state: { sorting, columnVisibility, rowSelection },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    getRowId: (row) => row.id,
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="page-title">
            Serwis
          </h1>
          <div className="flex items-center rounded-md border border-input overflow-hidden">
            <button
              className="flex items-center gap-1.5 bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground transition-colors"
            >
              <LayoutList size={14} />
              Lista
            </button>
            <button
              onClick={() => navigate("/service/board")}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors"
            >
              <Kanban size={14} />
              Tablica
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            entity="service_tickets"
            filters={{ search, ...filters }}
            visibleColumns={Object.keys(columnVisibility).filter((k) => columnVisibility[k] !== false)}
          />
          <Button onClick={() => navigate("/service/new")}>
            <Plus size={16} /> Nowe zgloszenie
          </Button>
        </div>
      </div>

      {/* Search + Column selector */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj..."
            className="h-9 w-full rounded-lg border border-input bg-transparent pl-9 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        <ColumnSelector
          columns={columnOptions}
          visibleCount={visibleCount}
          totalCount={totalCount}
          isColumnVisible={isColumnVisible}
          onToggleColumn={toggleColumn}
          onShowAll={showAll}
          onHideAll={hideAll}
        />
      </div>

      {/* Advanced Filters */}
      <FilterPanel activeCount={activeFilterCount} onClear={clearFilters}>
        <SelectFilter
          label="Status"
          value={filters.status || ""}
          onChange={(v) => setFilter("status", v)}
          options={[
            { value: "open", label: "Otwarte" },
            { value: "resolved", label: "Rozwiazane" },
            { value: "closed", label: "Zamkniete" },
          ]}
          placeholder="Wszystkie statusy"
        />
        <SelectFilter
          label="Priorytet"
          value={filters.priority || ""}
          onChange={(v) => setFilter("priority", v)}
          options={[
            { value: "urgent", label: "Pilny" },
            { value: "high", label: "Wysoki" },
            { value: "medium", label: "Sredni" },
            { value: "low", label: "Niski" },
          ]}
          placeholder="Wszystkie priorytety"
        />
        <DateRangeFilter
          label="Data utworzenia"
          fromValue={filters.created_at_from || ""}
          toValue={filters.created_at_to || ""}
          onFromChange={(v) => setFilter("created_at_from", v)}
          onToChange={(v) => setFilter("created_at_to", v)}
        />
      </FilterPanel>

      {/* Bulk action bar */}
      <BulkActionBar
        selectedCount={selectedCount}
        onDeselectAll={() => setRowSelection({})}
        actions={bulkActions}
      />

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : tickets.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-input py-12 text-muted-foreground">
          <p className="text-sm">Brak zgloszen serwisowych</p>
          <Button variant="outline" onClick={() => navigate("/service/new")}>
            <Plus size={16} /> Nowe zgloszenie
          </Button>
        </div>
      ) : (
        <div className="surface-card overflow-hidden">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-muted/30">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => navigate(buildDetailUrl("/service", row.original.id, { sort: sortingStateToParam(sorting), filters: filters as Record<string, string | undefined> }))}
                  className="cursor-pointer border-t transition-colors hover:bg-muted/30"
                  data-state={row.getIsSelected() ? "selected" : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LoadMoreButton
        hasMore={data?.meta.has_more ?? false}
        isLoading={isFetching && !!cursor}
        onClick={() => {
          if (data?.meta.next_cursor) setCursor(data.meta.next_cursor)
        }}
      />

      {/* Bulk action dialogs */}
      <BulkDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        count={selectedCount}
        entityLabel="zgloszen"
        onConfirm={handleBulkDelete}
      />
      <BulkAssignDialog
        open={showAssignDialog}
        onOpenChange={setShowAssignDialog}
        count={selectedCount}
        onConfirm={handleBulkAssign}
        fieldLabel="Przypisany"
      />
      <BulkStatusDialog
        open={showStatusDialog}
        onOpenChange={setShowStatusDialog}
        count={selectedCount}
        options={[
          { value: "open", label: "Otwarty" },
          { value: "resolved", label: "Rozwiazany" },
          { value: "closed", label: "Zamkniety" },
        ]}
        onConfirm={handleBulkStatus}
      />
      <BulkStatusDialog
        open={showPriorityDialog}
        onOpenChange={setShowPriorityDialog}
        count={selectedCount}
        options={[
          { value: "urgent", label: "Pilny" },
          { value: "high", label: "Wysoki" },
          { value: "medium", label: "Sredni" },
          { value: "low", label: "Niski" },
        ]}
        onConfirm={handleBulkPriority}
        title="Zmien priorytet"
        fieldLabel="Priorytet"
      />
      <BulkTagDialog
        open={showTagDialog}
        onOpenChange={setShowTagDialog}
        count={selectedCount}
        onConfirm={handleBulkTag}
      />
    </div>
  )
}
